/**
 * Email Sequences Domain Seed Data
 *
 * Contains all seed data for Email Marketing:
 * - Email Sequences (drip campaigns)
 * - Sequence Steps (individual emails in sequence)
 * - Sequence Enrollees (contacts in sequences)
 * - Campaigns (bulk email/voice broadcasts)
 *
 * @see KB Articles: kb-business-email-sequences, kb-business-campaigns
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// EMAIL SEQUENCE IDs
// =============================================================================

export const EMAIL_SEQUENCE_IDS = {
  // Fiops Capital Sequences
  NEW_LEAD_NURTURE: seedId('seq-new-lead', 1),
  STALLED_APPLICATION: seedId('seq-stalled-app', 1),
  POST_FUNDING_FOLLOW_UP: seedId('seq-post-fund', 1),
  REFERRAL_PARTNER_ONBOARD: seedId('seq-partner-onboard', 1),
  REPEAT_BORROWER: seedId('seq-repeat', 1),

  // 1Sharpe Capital Sequences
  SHARPE_CONSTRUCTION_INTRO: seedId('seq-sharpe-constr', 1),
  SHARPE_INVESTOR_UPDATE: seedId('seq-sharpe-investor', 1),
} as const;

export type EmailSequenceId = (typeof EMAIL_SEQUENCE_IDS)[keyof typeof EMAIL_SEQUENCE_IDS];

// =============================================================================
// SEQUENCE STEP IDs
// =============================================================================

export const SEQUENCE_STEP_IDS = {
  // New Lead Nurture Steps
  NEW_LEAD_WELCOME: seedId('step-lead-welcome', 1),
  NEW_LEAD_VALUE_PROP: seedId('step-lead-value', 1),
  NEW_LEAD_CASE_STUDY: seedId('step-lead-case', 1),
  NEW_LEAD_CTA: seedId('step-lead-cta', 1),

  // Stalled Application Steps
  STALLED_REMINDER: seedId('step-stalled-remind', 1),
  STALLED_OFFER_HELP: seedId('step-stalled-help', 1),
  STALLED_FINAL: seedId('step-stalled-final', 1),

  // Post Funding Steps
  POST_FUND_THANK: seedId('step-postfund-thank', 1),
  POST_FUND_REVIEW: seedId('step-postfund-review', 1),
  POST_FUND_REFERRAL: seedId('step-postfund-ref', 1),
} as const;

export type SequenceStepId = (typeof SEQUENCE_STEP_IDS)[keyof typeof SEQUENCE_STEP_IDS];

// =============================================================================
// CAMPAIGN IDs
// =============================================================================

export const CAMPAIGN_IDS = {
  // Fiops Capital Campaigns
  Q1_RATE_ANNOUNCEMENT: seedId('campaign-q1-rates', 1),
  NEW_PRODUCT_LAUNCH: seedId('campaign-product', 1),
  HOLIDAY_GREETING: seedId('campaign-holiday', 1),

  // Voice Campaigns
  VOICE_PAYMENT_REMINDER: seedId('campaign-voice-payment', 1),
} as const;

export type CampaignId = (typeof CAMPAIGN_IDS)[keyof typeof CAMPAIGN_IDS];

// =============================================================================
// SEQUENCE STATUS & STEP TYPES
// =============================================================================

export const SEQUENCE_STATUSES = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  ARCHIVED: 'ARCHIVED',
} as const;

export type SeedSequenceStatus = (typeof SEQUENCE_STATUSES)[keyof typeof SEQUENCE_STATUSES];

export const STEP_TYPES = {
  EMAIL: 'EMAIL',
  DELAY: 'DELAY',
  CONDITION: 'CONDITION',
  TASK: 'TASK',
  WEBHOOK: 'WEBHOOK',
} as const;

export type StepType = (typeof STEP_TYPES)[keyof typeof STEP_TYPES];

export const ENROLLEE_STATUSES = {
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  PAUSED: 'PAUSED',
  BOUNCED: 'BOUNCED',
  UNSUBSCRIBED: 'UNSUBSCRIBED',
  CONVERTED: 'CONVERTED',
} as const;

export type EnrolleeStatus = (typeof ENROLLEE_STATUSES)[keyof typeof ENROLLEE_STATUSES];

export const CAMPAIGN_TYPES = {
  EMAIL: 'EMAIL',
  VOICE: 'VOICE',
  SMS: 'SMS',
} as const;

export type CampaignType = (typeof CAMPAIGN_TYPES)[keyof typeof CAMPAIGN_TYPES];

export const SEED_CAMPAIGN_STATUSES = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  SENDING: 'SENDING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;

export type SeedCampaignStatus = (typeof SEED_CAMPAIGN_STATUSES)[keyof typeof SEED_CAMPAIGN_STATUSES];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface EmailSequenceSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  status: SeedSequenceStatus;
  triggerType: 'manual' | 'event' | 'segment';
  triggerConfig?: Record<string, unknown>;
  goalType?: 'conversion' | 'engagement' | 'reply';
  goalConfig?: Record<string, unknown>;
  enrollmentLimit?: number;
  exitConditions?: Record<string, unknown>[];
  timezone: string;
  sendOnWeekends: boolean;
  sendHoursStart: number; // 0-23
  sendHoursEnd: number; // 0-23
  createdBy: string;
  isActive: boolean;
}

export interface SequenceStepSeedData {
  id: string;
  sequenceId: string;
  stepType: StepType;
  order: number;
  name: string;
  delayDays?: number;
  delayHours?: number;
  emailSubject?: string;
  emailBody?: string;
  emailTemplateId?: string;
  condition?: Record<string, unknown>;
  taskTitle?: string;
  taskAssignee?: string;
  webhookUrl?: string;
  webhookPayload?: Record<string, unknown>;
  isActive: boolean;
}

export interface SequenceEnrolleeSeedData {
  id: string;
  sequenceId: string;
  contactId: string;
  contactType: 'lead' | 'consumer' | 'partner';
  email: string;
  status: EnrolleeStatus;
  currentStepIndex: number;
  enrolledAt: string;
  enrolledBy?: string;
  lastActivityAt?: string;
  nextScheduledAt?: string;
  completedAt?: string;
  convertedAt?: string;
  pausedAt?: string;
  pauseReason?: string;
  metadata?: Record<string, unknown>;
}

export interface CampaignSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  campaignType: CampaignType;
  status: SeedCampaignStatus;
  subject?: string;
  content?: string;
  templateId?: string;
  voiceScriptId?: string;
  fromName?: string;
  fromEmail?: string;
  replyTo?: string;
  segmentId?: string;
  segmentQuery?: Record<string, unknown>;
  scheduledAt?: string;
  startedAt?: string;
  completedAt?: string;
  totalRecipients?: number;
  delivered?: number;
  opened?: number;
  clicked?: number;
  bounced?: number;
  unsubscribed?: number;
  createdBy: string;
}

// =============================================================================
// EMAIL SEQUENCE DATA
// =============================================================================

export const EMAIL_SEQUENCES: Record<keyof typeof EMAIL_SEQUENCE_IDS, EmailSequenceSeedData> = {
  NEW_LEAD_NURTURE: {
    id: EMAIL_SEQUENCE_IDS.NEW_LEAD_NURTURE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'New Lead Nurture',
    description: 'Welcome sequence for new leads to educate about our products and drive applications',
    status: SEQUENCE_STATUSES.ACTIVE,
    triggerType: 'event',
    triggerConfig: { event: 'lead.created', source: ['website', 'referral'] },
    goalType: 'conversion',
    goalConfig: { action: 'application.submitted' },
    enrollmentLimit: 1, // Once per contact
    timezone: 'America/Los_Angeles',
    sendOnWeekends: false,
    sendHoursStart: 9,
    sendHoursEnd: 17,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    isActive: true,
  },
  STALLED_APPLICATION: {
    id: EMAIL_SEQUENCE_IDS.STALLED_APPLICATION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Stalled Application Follow-up',
    description: "Re-engage borrowers who started but haven't completed their application",
    status: SEQUENCE_STATUSES.ACTIVE,
    triggerType: 'segment',
    triggerConfig: { segment: 'stalled_applications', stalledDays: 3 },
    goalType: 'conversion',
    goalConfig: { action: 'application.completed' },
    exitConditions: [
      { field: 'applicationStatus', operator: 'eq', value: 'completed' },
      { field: 'applicationStatus', operator: 'eq', value: 'withdrawn' },
    ],
    timezone: 'America/Los_Angeles',
    sendOnWeekends: false,
    sendHoursStart: 10,
    sendHoursEnd: 16,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    isActive: true,
  },
  POST_FUNDING_FOLLOW_UP: {
    id: EMAIL_SEQUENCE_IDS.POST_FUNDING_FOLLOW_UP,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Post-Funding Follow-up',
    description: 'Thank borrowers and request referrals after successful funding',
    status: SEQUENCE_STATUSES.ACTIVE,
    triggerType: 'event',
    triggerConfig: { event: 'loan.funded' },
    goalType: 'engagement',
    goalConfig: { action: 'review.submitted' },
    timezone: 'America/Los_Angeles',
    sendOnWeekends: true,
    sendHoursStart: 8,
    sendHoursEnd: 20,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    isActive: true,
  },
  REFERRAL_PARTNER_ONBOARD: {
    id: EMAIL_SEQUENCE_IDS.REFERRAL_PARTNER_ONBOARD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Referral Partner Onboarding',
    description: 'Onboard new referral partners with training and resources',
    status: SEQUENCE_STATUSES.ACTIVE,
    triggerType: 'event',
    triggerConfig: { event: 'partner.approved' },
    goalType: 'engagement',
    goalConfig: { action: 'referral.submitted' },
    timezone: 'America/Los_Angeles',
    sendOnWeekends: false,
    sendHoursStart: 9,
    sendHoursEnd: 17,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    isActive: true,
  },
  REPEAT_BORROWER: {
    id: EMAIL_SEQUENCE_IDS.REPEAT_BORROWER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Repeat Borrower Outreach',
    description: 'Engage past borrowers for repeat business',
    status: SEQUENCE_STATUSES.ACTIVE,
    triggerType: 'segment',
    triggerConfig: { segment: 'past_borrowers', daysSinceFunding: 180 },
    goalType: 'conversion',
    goalConfig: { action: 'application.submitted' },
    timezone: 'America/Los_Angeles',
    sendOnWeekends: false,
    sendHoursStart: 10,
    sendHoursEnd: 16,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    isActive: true,
  },
  SHARPE_CONSTRUCTION_INTRO: {
    id: EMAIL_SEQUENCE_IDS.SHARPE_CONSTRUCTION_INTRO,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Construction Financing Introduction',
    description: 'Educate new leads about construction financing options',
    status: SEQUENCE_STATUSES.ACTIVE,
    triggerType: 'event',
    triggerConfig: { event: 'lead.created', productInterest: 'construction' },
    goalType: 'conversion',
    timezone: 'America/New_York',
    sendOnWeekends: false,
    sendHoursStart: 9,
    sendHoursEnd: 18,
    createdBy: USER_IDS.SHARPE_ADMIN,
    isActive: true,
  },
  SHARPE_INVESTOR_UPDATE: {
    id: EMAIL_SEQUENCE_IDS.SHARPE_INVESTOR_UPDATE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Quarterly Investor Update',
    description: 'Quarterly updates to fund investors',
    status: SEQUENCE_STATUSES.DRAFT,
    triggerType: 'manual',
    timezone: 'America/New_York',
    sendOnWeekends: false,
    sendHoursStart: 10,
    sendHoursEnd: 14,
    createdBy: USER_IDS.SHARPE_ADMIN,
    isActive: false,
  },
};

// =============================================================================
// SEQUENCE STEP DATA
// =============================================================================

export const SEQUENCE_STEPS: Record<keyof typeof SEQUENCE_STEP_IDS, SequenceStepSeedData> = {
  // New Lead Nurture Steps
  NEW_LEAD_WELCOME: {
    id: SEQUENCE_STEP_IDS.NEW_LEAD_WELCOME,
    sequenceId: EMAIL_SEQUENCE_IDS.NEW_LEAD_NURTURE,
    stepType: STEP_TYPES.EMAIL,
    order: 1,
    name: 'Welcome Email',
    emailSubject: "Welcome to Fiops Capital - Let's Get You Funded",
    emailBody: 'Hi {{firstName}},\n\nThank you for your interest in Fiops Capital...',
    isActive: true,
  },
  NEW_LEAD_VALUE_PROP: {
    id: SEQUENCE_STEP_IDS.NEW_LEAD_VALUE_PROP,
    sequenceId: EMAIL_SEQUENCE_IDS.NEW_LEAD_NURTURE,
    stepType: STEP_TYPES.EMAIL,
    order: 2,
    name: 'Value Proposition',
    delayDays: 2,
    emailSubject: 'Why Real Estate Investors Choose Fiops',
    emailBody: 'Hi {{firstName}},\n\nWhen it comes to bridge and fix-flip financing...',
    isActive: true,
  },
  NEW_LEAD_CASE_STUDY: {
    id: SEQUENCE_STEP_IDS.NEW_LEAD_CASE_STUDY,
    sequenceId: EMAIL_SEQUENCE_IDS.NEW_LEAD_NURTURE,
    stepType: STEP_TYPES.EMAIL,
    order: 3,
    name: 'Case Study',
    delayDays: 3,
    emailSubject: 'How Marcus Scaled to 15 Properties with Our Help',
    emailBody: 'Hi {{firstName}},\n\nI wanted to share a success story from one of our investors...',
    isActive: true,
  },
  NEW_LEAD_CTA: {
    id: SEQUENCE_STEP_IDS.NEW_LEAD_CTA,
    sequenceId: EMAIL_SEQUENCE_IDS.NEW_LEAD_NURTURE,
    stepType: STEP_TYPES.EMAIL,
    order: 4,
    name: 'Call to Action',
    delayDays: 4,
    emailSubject: "Ready to Get Started? Here's Your Next Step",
    emailBody: "Hi {{firstName}},\n\nI noticed you haven't started your application yet...",
    isActive: true,
  },

  // Stalled Application Steps
  STALLED_REMINDER: {
    id: SEQUENCE_STEP_IDS.STALLED_REMINDER,
    sequenceId: EMAIL_SEQUENCE_IDS.STALLED_APPLICATION,
    stepType: STEP_TYPES.EMAIL,
    order: 1,
    name: 'Friendly Reminder',
    emailSubject: 'Your Application is Waiting - Pick Up Where You Left Off',
    emailBody: "Hi {{firstName}},\n\nWe noticed you started your application but haven't finished...",
    isActive: true,
  },
  STALLED_OFFER_HELP: {
    id: SEQUENCE_STEP_IDS.STALLED_OFFER_HELP,
    sequenceId: EMAIL_SEQUENCE_IDS.STALLED_APPLICATION,
    stepType: STEP_TYPES.EMAIL,
    order: 2,
    name: 'Offer Help',
    delayDays: 3,
    emailSubject: "Need Help With Your Application? We're Here",
    emailBody: 'Hi {{firstName}},\n\nSometimes applications can get complicated...',
    isActive: true,
  },
  STALLED_FINAL: {
    id: SEQUENCE_STEP_IDS.STALLED_FINAL,
    sequenceId: EMAIL_SEQUENCE_IDS.STALLED_APPLICATION,
    stepType: STEP_TYPES.EMAIL,
    order: 3,
    name: 'Final Follow-up',
    delayDays: 7,
    emailSubject: 'Still Interested in Financing?',
    emailBody: 'Hi {{firstName}},\n\nThis will be my last email about your application...',
    isActive: true,
  },

  // Post Funding Steps
  POST_FUND_THANK: {
    id: SEQUENCE_STEP_IDS.POST_FUND_THANK,
    sequenceId: EMAIL_SEQUENCE_IDS.POST_FUNDING_FOLLOW_UP,
    stepType: STEP_TYPES.EMAIL,
    order: 1,
    name: 'Thank You',
    emailSubject: 'Congratulations on Your Funding! 🎉',
    emailBody:
      "Hi {{firstName}},\n\nCongratulations on closing your loan! We're thrilled to have been part of your journey...",
    isActive: true,
  },
  POST_FUND_REVIEW: {
    id: SEQUENCE_STEP_IDS.POST_FUND_REVIEW,
    sequenceId: EMAIL_SEQUENCE_IDS.POST_FUNDING_FOLLOW_UP,
    stepType: STEP_TYPES.EMAIL,
    order: 2,
    name: 'Request Review',
    delayDays: 14,
    emailSubject: 'How Was Your Experience?',
    emailBody: "Hi {{firstName}},\n\nNow that you've had some time with your new investment...",
    isActive: true,
  },
  POST_FUND_REFERRAL: {
    id: SEQUENCE_STEP_IDS.POST_FUND_REFERRAL,
    sequenceId: EMAIL_SEQUENCE_IDS.POST_FUNDING_FOLLOW_UP,
    stepType: STEP_TYPES.EMAIL,
    order: 3,
    name: 'Referral Request',
    delayDays: 30,
    emailSubject: 'Know Someone Who Could Use Our Help?',
    emailBody: 'Hi {{firstName}},\n\nWe hope your investment is going well! If you know any fellow investors...',
    isActive: true,
  },
};

// =============================================================================
// CAMPAIGN DATA
// =============================================================================

export const CAMPAIGNS: Record<keyof typeof CAMPAIGN_IDS, CampaignSeedData> = {
  Q1_RATE_ANNOUNCEMENT: {
    id: CAMPAIGN_IDS.Q1_RATE_ANNOUNCEMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Q1 2026 Rate Announcement',
    description: 'Announce new competitive rates for Q1',
    campaignType: CAMPAIGN_TYPES.EMAIL,
    status: SEED_CAMPAIGN_STATUSES.COMPLETED,
    subject: 'New Lower Rates for Q1 2026 - As Low as 9.99%',
    fromName: 'Rachel Martinez',
    fromEmail: 'rachel@opsmatrix.local',
    segmentQuery: { status: 'active', lastActivityDays: 90 },
    scheduledAt: '2026-01-02T10:00:00Z',
    startedAt: '2026-01-02T10:00:00Z',
    completedAt: '2026-01-02T10:45:00Z',
    totalRecipients: 2500,
    delivered: 2450,
    opened: 875,
    clicked: 234,
    bounced: 50,
    unsubscribed: 12,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  NEW_PRODUCT_LAUNCH: {
    id: CAMPAIGN_IDS.NEW_PRODUCT_LAUNCH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Ground-Up Construction Product Launch',
    description: 'Announce new ground-up construction financing program',
    campaignType: CAMPAIGN_TYPES.EMAIL,
    status: SEED_CAMPAIGN_STATUSES.SCHEDULED,
    subject: 'Introducing: Ground-Up Construction Financing',
    fromName: 'Fiops Capital',
    fromEmail: 'info@opsmatrix.local',
    segmentQuery: { type: 'investor', experience: 'advanced' },
    scheduledAt: '2026-02-01T10:00:00Z',
    totalRecipients: 1200,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  HOLIDAY_GREETING: {
    id: CAMPAIGN_IDS.HOLIDAY_GREETING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Holiday Greeting 2025',
    description: 'End of year thank you and holiday wishes',
    campaignType: CAMPAIGN_TYPES.EMAIL,
    status: SEED_CAMPAIGN_STATUSES.COMPLETED,
    subject: 'Happy Holidays from Fiops Capital! 🎄',
    fromName: 'The Fiops Team',
    fromEmail: 'team@opsmatrix.local',
    completedAt: '2025-12-23T09:00:00Z',
    totalRecipients: 5000,
    delivered: 4920,
    opened: 2100,
    clicked: 450,
    bounced: 80,
    unsubscribed: 25,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  VOICE_PAYMENT_REMINDER: {
    id: CAMPAIGN_IDS.VOICE_PAYMENT_REMINDER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Payment Reminder Voice Campaign',
    description: 'Automated voice reminders for upcoming payments',
    campaignType: CAMPAIGN_TYPES.VOICE,
    status: SEED_CAMPAIGN_STATUSES.DRAFT,
    segmentQuery: { paymentDueDays: 7, preferredContact: 'phone' },
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
};

// =============================================================================
// SEQUENCE ENROLLEE IDs
// =============================================================================

export const SEQUENCE_ENROLLEE_IDS = {
  MARCUS_NEW_LEAD: seedId('enrollee-marcus', 1),
  DAVID_STALLED: seedId('enrollee-david', 1),
  ROBERT_POST_FUND: seedId('enrollee-robert', 1),
} as const;

export type SequenceEnrolleeId = (typeof SEQUENCE_ENROLLEE_IDS)[keyof typeof SEQUENCE_ENROLLEE_IDS];

export const SEQUENCE_ENROLLEES: Record<keyof typeof SEQUENCE_ENROLLEE_IDS, SequenceEnrolleeSeedData> = {
  MARCUS_NEW_LEAD: {
    id: SEQUENCE_ENROLLEE_IDS.MARCUS_NEW_LEAD,
    sequenceId: EMAIL_SEQUENCE_IDS.NEW_LEAD_NURTURE,
    contactId: 'consumer-marcus', // Reference to consumer
    contactType: 'consumer',
    email: 'marcus.thompson@investorpro.com',
    status: ENROLLEE_STATUSES.CONVERTED,
    currentStepIndex: 4,
    enrolledAt: '2025-12-15T10:00:00Z',
    lastActivityAt: '2025-12-23T14:30:00Z',
    completedAt: '2025-12-23T14:30:00Z',
    convertedAt: '2025-12-28T11:00:00Z',
    metadata: { source: 'website', landingPage: 'bridge-loans' },
  },
  DAVID_STALLED: {
    id: SEQUENCE_ENROLLEE_IDS.DAVID_STALLED,
    sequenceId: EMAIL_SEQUENCE_IDS.STALLED_APPLICATION,
    contactId: 'consumer-david',
    contactType: 'consumer',
    email: 'david.chen@techventures.io',
    status: ENROLLEE_STATUSES.ACTIVE,
    currentStepIndex: 2,
    enrolledAt: '2026-01-15T09:00:00Z',
    lastActivityAt: '2026-01-18T10:15:00Z',
    nextScheduledAt: '2026-01-22T10:00:00Z',
    metadata: { stalledStage: 'document_collection' },
  },
  ROBERT_POST_FUND: {
    id: SEQUENCE_ENROLLEE_IDS.ROBERT_POST_FUND,
    sequenceId: EMAIL_SEQUENCE_IDS.POST_FUNDING_FOLLOW_UP,
    contactId: 'consumer-robert',
    contactType: 'consumer',
    email: 'robert.garcia@sunbeltholdings.com',
    status: ENROLLEE_STATUSES.ACTIVE,
    currentStepIndex: 1,
    enrolledAt: '2026-01-10T15:00:00Z',
    lastActivityAt: '2026-01-10T15:00:00Z',
    nextScheduledAt: '2026-01-24T10:00:00Z',
    metadata: { loanAmount: 850000, fundingDate: '2026-01-10' },
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllEmailSequences(workspace?: string): EmailSequenceSeedData[] {
  const sequences = Object.values(EMAIL_SEQUENCES);
  if (!workspace) return sequences;
  return sequences.map((s) => ({ ...s, name: `${s.name} [${workspace}]` }));
}

export function getAllSequenceSteps(): SequenceStepSeedData[] {
  return Object.values(SEQUENCE_STEPS);
}

export function getAllSequenceEnrollees(): SequenceEnrolleeSeedData[] {
  return Object.values(SEQUENCE_ENROLLEES);
}

export function getAllCampaigns(workspace?: string): CampaignSeedData[] {
  const campaigns = Object.values(CAMPAIGNS);
  if (!workspace) return campaigns;
  return campaigns.map((c) => ({ ...c, name: `${c.name} [${workspace}]` }));
}

export function getSequencesByBusiness(businessId: string): EmailSequenceSeedData[] {
  return Object.values(EMAIL_SEQUENCES).filter((s) => s.businessId === businessId);
}

export function getStepsBySequence(sequenceId: string): SequenceStepSeedData[] {
  return Object.values(SEQUENCE_STEPS)
    .filter((s) => s.sequenceId === sequenceId)
    .sort((a, b) => a.order - b.order);
}

export function getEnrolleesBySequence(sequenceId: string): SequenceEnrolleeSeedData[] {
  return Object.values(SEQUENCE_ENROLLEES).filter((e) => e.sequenceId === sequenceId);
}

export function getCampaignsByBusiness(businessId: string): CampaignSeedData[] {
  return Object.values(CAMPAIGNS).filter((c) => c.businessId === businessId);
}

export function getActiveSequences(): EmailSequenceSeedData[] {
  return Object.values(EMAIL_SEQUENCES).filter((s) => s.status === SEQUENCE_STATUSES.ACTIVE);
}

export function getActiveEnrollees(): SequenceEnrolleeSeedData[] {
  return Object.values(SEQUENCE_ENROLLEES).filter((e) => e.status === ENROLLEE_STATUSES.ACTIVE);
}
